import { useEffect, useState, useCallback } from "react";
import { MemberCancellation } from "../../../model/members/MemberCancellation";
import dayjs from "dayjs";
import { Card, Skeleton, Table, Tag, Button, Space } from "antd";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { EditOutlined } from "@ant-design/icons";
import type { SortOrder } from 'antd/es/table/interface';
import { useClient } from "../../../common/ClientProvider";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import { DEFAULT_CANCELLATION_REASONS } from "../../../model/client/ClientSettings";
import { CreateMemberCancellationRequest } from "../../../requests/member/CreateMemberCancellationRequest";
import { notification } from "antd";
import CancellationModal from "./CancellationModal";

interface MemberCancellationsViewProps {
    membershipId: string;
    backgroundColor?: string;
    borderColor?: string;
    // When false, the cancellation list is view-only: edit / mark-inactive
    // actions are disabled (e.g. front-of-house). Defaults to true.
    canManage?: boolean;
}

const MemberCancellationsView: React.FC<MemberCancellationsViewProps> = ({
    membershipId,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb',
    canManage = true
}) => {
    const [loading, setLoading] = useState(true);
    const [cancellations, setCancellations] = useState<MemberCancellation[]>([]);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingCancellation, setEditingCancellation] = useState<MemberCancellation | null>(null);
    const memberService = useMemberService();
    const client = useClient();
    const { cancellation: cancellationConfig } = useClientConfig();
    const reasonLabel = (value: string) =>
        (cancellationConfig?.reasons ?? DEFAULT_CANCELLATION_REASONS).find((o) => o.value === value)?.label
        ?? value;

    const fetchCancellations = useCallback(async () => {
        if (!membershipId) return;
        try {
            const data = await memberService.getCancellations(membershipId);
            setCancellations(data);
        } catch (error) {
            console.error("Error fetching cancellations:", error);
            setCancellations([]);
        } finally {
            setLoading(false);
        }
    }, [membershipId, memberService]);

    useEffect(() => {
        fetchCancellations();
    }, [fetchCancellations]);

    const handleMarkInactive = async (cancellation: MemberCancellation) => {
        try {
            const request: CreateMemberCancellationRequest = {
                membershipId: cancellation.membershipId,
                clientName: client.currentClientId,
                cancellationDate: cancellation.cancellationDate,
                cancellationActive: false,
                cancellationReason: cancellation.reason,
                // The upsert writes notes unconditionally, so omitting this would blank whatever
                // the operator recorded on the cancellation just by marking it inactive.
                cancellationNotes: cancellation.notes,
            };

            await memberService.cancelMembership(request);
            notification.success({
                message: "Success",
                description: "Cancellation marked as inactive",
            });
            fetchCancellations();
        } catch (error) {
            notification.error({
                message: "Error",
                description: "Failed to update cancellation",
            });
        }
    };

    const getRowStyle = (cancellation: MemberCancellation) => {
        const today = dayjs();
        const cancellationDate = dayjs(cancellation.cancellationDate);
        const isPast = cancellationDate.isBefore(today, 'day');
        const isActive = cancellation.cancellationActive;

        // Active cancellations should be highlighted
        if (isActive && !isPast) {
            return {
                backgroundColor: '#fff7e6', // Light orange/yellow for active
            };
        }

        // Past cancellations or inactive should be grey
        if (isPast || !isActive) {
            return {
                backgroundColor: '#f5f5f5', // Grey
                color: '#8c8c8c',
            };
        }

        return {};
    };

    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    const columns = [
        {
            title: 'Cancellation Date',
            dataIndex: 'cancellationDate',
            key: 'cancellationDate',
            render: (date: string) => formatDate(date),
            sorter: (a: MemberCancellation, b: MemberCancellation) => 
                dayjs(a.cancellationDate).unix() - dayjs(b.cancellationDate).unix(),
            defaultSortOrder: 'descend' as SortOrder
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: MemberCancellation) => {
                const isActive = record.cancellationActive;
                return (
                    <Tag color={isActive ? 'green' : 'default'}>
                        {isActive ? 'Active' : 'Inactive'}
                    </Tag>
                );
            },
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
            // Stored as an option code; show the client's configured wording. Historic rows hold
            // free text that matches no option, so fall through to the raw value.
            render: (reason: string) => reasonLabel(reason),
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            ellipsis: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: MemberCancellation) => (
                <Space>
                    <Button
                        type="default"
                        icon={<EditOutlined />}
                        disabled={!canManage}
                        onClick={() => {
                            setEditingCancellation(record);
                            setIsEditModalVisible(true);
                        }}
                    >
                        Edit
                    </Button>
                    {record.cancellationActive && (
                        <Button
                            type="default"
                            danger
                            disabled={!canManage}
                            onClick={() => handleMarkInactive(record)}
                        >
                            Mark Inactive
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    if (loading) {
        return <Skeleton active />;
    }

    return (
        <Card 
            title="Cancellations"
            className="mb-4"
            style={{ 
                backgroundColor,
                borderColor
            }}
        >
            <Table
                columns={columns}
                dataSource={cancellations}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 10 }}
                rowKey={(record, index) => `${record.cancellationDate}-${record.membershipId}-${index}`}
                rowClassName={(record) => {
                    const today = dayjs();
                    const cancellationDate = dayjs(record.cancellationDate);
                    const isPast = cancellationDate.isBefore(today, 'day');
                    const isActive = record.cancellationActive;
                    
                    if (isPast || !isActive) {
                        return 'cancellation-row-grey';
                    }
                    return '';
                }}
                onRow={(record) => ({
                    style: getRowStyle(record),
                })}
                locale={{
                    emptyText: 'No cancellations found'
                }}
            />
            <style>{`
                .cancellation-row-grey {
                    color: #8c8c8c;
                }
            `}</style>
            <CancellationModal
                open={isEditModalVisible}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setEditingCancellation(null);
                }}
                onSuccess={() => {
                    setIsEditModalVisible(false);
                    setEditingCancellation(null);
                    fetchCancellations();
                }}
                membershipId={membershipId}
                editingCancellation={editingCancellation}
            />
        </Card>
    );
};

export default MemberCancellationsView;

